import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Chip,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SafeHtml } from './renderers/SafeHtml';
import { MoodleDeployment } from '../../../types/moodleTypes';

import {
  useMoodleCourseContent,
  useMoodlePagesByCourses,
} from '../../../hooks/useMoodleHooks';

interface Props {
  moodleCourseId: number;
  moodleUrl: string;
  deploymentState: 'staging' | 'production';
  deploymentInfo: MoodleDeployment;
  onLoadError?: (err: Error) => void;
}

interface MoodleSection {
  id: number;
  name: string;
  section: number;
  summary: string;
  modules: MoodleModule[];
}

interface MoodleModule {
  id: number;
  instance: number;
  name: string;
  modname: string;
  url?: string;
  description?: string;
  visible: number;
}

interface MoodlePage {
  id: number;
  coursemodule: number;
  course: number;
  name: string;
  intro: string;
  content: string;
  contentformat: number;
  timemodified: number;
}

export const DeployedCoursePreview: React.FC<Props> = ({
                                                         moodleCourseId,
                                                         moodleUrl,
                                                         deploymentState,
                                                         deploymentInfo,
                                                         onLoadError,
                                                       }) => {
  const enabled = Boolean(moodleCourseId && moodleCourseId > 0);

  const {
    data: sectionsRaw,
    isLoading: isLoadingStructure,
    error: structureError,
  } = useMoodleCourseContent(moodleCourseId, { enabled }, 'internal');

  const {
    data: pagesRaw,
    isLoading: isLoadingPages,
    error: pagesError,
  } = useMoodlePagesByCourses(moodleCourseId, undefined, 'internal');

  const sections = useMemo<MoodleSection[]>(() => {
    const raw = sectionsRaw as any;
    if (Array.isArray(raw)) return raw as MoodleSection[];
    if (Array.isArray(raw?.sections)) return raw.sections as MoodleSection[];
    if (Array.isArray(raw?.data)) return raw.data as MoodleSection[];
    return [];
  }, [sectionsRaw]);

  const pages = useMemo<MoodlePage[]>(() => {
    const raw = pagesRaw as any;
    if (Array.isArray(raw)) return raw as MoodlePage[];
    if (Array.isArray(raw?.pages)) return raw.pages as MoodlePage[];
    if (Array.isArray(raw?.data)) return raw.data as MoodlePage[];
    return [];
  }, [pagesRaw]);

  React.useEffect(() => {
    if (isLoadingStructure || isLoadingPages) return;
    if (structureError || pagesError) return;
    if (sections.length === 0) {
      onLoadError?.(
        new Error(
          `No course content returned for ${deploymentState} (courseId=${moodleCourseId})`
        )
      );
    }
  }, [
    isLoadingStructure,
    isLoadingPages,
    structureError,
    pagesError,
    sections.length,
    onLoadError,
    deploymentState,
    moodleCourseId,
  ]);

  const pagesById = useMemo<Record<number, MoodlePage>>(() => {
    const map: Record<number, MoodlePage> = {};
    for (const p of pages) {
      if (p && typeof p.id === 'number') map[p.id] = p;
    }
    return map;
  }, [pages]);

  const [openSectionId, setOpenSectionId] = useState<number | null>(null);

  React.useEffect(() => {
    if (!sections.length) {
      setOpenSectionId(null);
      return;
    }

    // Keep current selection if still valid
    if (openSectionId != null && sections.some((s) => s.id === openSectionId)) return;

    const defaultId = sections.find((s) => s.section === 0)?.id ?? sections[0]?.id ?? null;
    setOpenSectionId(defaultId);
  }, [sections, openSectionId]);

  // Track open page accordion per section (so within a section only 1 page is open)
  const [openPageIdBySection, setOpenPageIdBySection] = useState<Record<number, number | null>>(
    {}
  );

  const handleToggleSection = useCallback(
    (sectionId: number) => (_: React.SyntheticEvent, expanded: boolean) => {
      setOpenSectionId(expanded ? sectionId : null);
    },
    []
  );

  const handleTogglePage = useCallback(
    (sectionId: number, moduleId: number) => (_: React.SyntheticEvent, expanded: boolean) => {
      setOpenPageIdBySection((prev) => ({
        ...prev,
        [sectionId]: expanded ? moduleId : null,
      }));
    },
    []
  );

  if (isLoadingStructure || isLoadingPages) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading {deploymentState} course content...</Typography>
      </Box>
    );
  }

  if (structureError || pagesError) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        <strong>Error Loading {deploymentState} Course:</strong>{' '}
        {(structureError as Error)?.message ||
          (pagesError as Error)?.message ||
          'Unknown error occurred'}
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" display="block">
            Moodle URL: {moodleUrl}
          </Typography>
          <Typography variant="caption" display="block">
            Course ID: {moodleCourseId}
          </Typography>
        </Box>
      </Alert>
    );
  }

  if (sections.length === 0) {
    return (
      <Alert severity="warning" sx={{ m: 3 }}>
        No course content available in {deploymentState} environment.
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" display="block">
            Moodle URL: {moodleUrl}
          </Typography>
          <Typography variant="caption" display="block">
            Course ID: {moodleCourseId}
          </Typography>
        </Box>
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Deployment Info Banner */}
      <Alert
        severity={deploymentState === 'staging' ? 'warning' : 'success'}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            <strong>
              {deploymentState === 'staging' ? 'Staging Environment' : 'Production Environment'}
            </strong>{' '}
            – Live Content
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap" sx={{ mt: 1 }}>
            <Chip label={`Course ID: ${moodleCourseId}`} size="small" variant="outlined" />
            <Chip
              label={`Moodle: ${new URL(moodleUrl).hostname}`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`Deployed: ${new Date((deploymentInfo as any).deployed_at).toLocaleString()}`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={(deploymentInfo as any).status}
              size="small"
              color={
                (deploymentInfo as any).status === 'active'
                  ? 'success'
                  : (deploymentInfo as any).status === 'failed'
                    ? 'error'
                    : 'warning'
              }
            />
          </Box>
        </Box>
      </Alert>

      {/* Course Sections (single-open) */}
      {sections.map((section) => {
        const modules: MoodleModule[] = Array.isArray(section.modules) ? section.modules : [];
        const expandedSection = openSectionId === section.id;

        return (
          <Accordion
            key={section.id}
            expanded={expandedSection}
            onChange={handleToggleSection(section.id)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: 'grey.50', '&:hover': { bgcolor: 'grey.100' } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6">
                  {section.name || `Section ${section.section}`}
                </Typography>
                <Chip
                  label={`${modules.length} activities`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 3 }}>
              {section.summary && (
                <Box sx={{ mb: 3 }}>
                  <SafeHtml html={section.summary} />
                </Box>
              )}

              {modules.map((module) => {
                if (module.visible === 0) return null;

                if (module.modname === 'page') {
                  const page = pagesById[module.instance];

                  if (!page) {
                    return (
                      <Alert severity="warning" sx={{ mb: 2 }} key={module.id}>
                        <strong>{module.name}</strong> – Content not found (Activity ID:{' '}
                        {module.instance})
                      </Alert>
                    );
                  }

                  const expandedPage = openPageIdBySection[section.id] === module.id;

                  return (
                    <Accordion
                      key={module.id}
                      expanded={expandedPage}
                      onChange={handleTogglePage(section.id, module.id)}
                      disableGutters
                      elevation={0}
                      sx={{
                        mb: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderLeft: '4px solid',
                        borderLeftColor:
                          deploymentState === 'staging' ? 'warning.main' : 'success.main',
                        '&:before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {page.name}
                          </Typography>
                          <Chip
                            label="Page"
                            size="small"
                            color={deploymentState === 'staging' ? 'warning' : 'success'}
                          />
                        </Box>
                      </AccordionSummary>

                      <AccordionDetails sx={{ pt: 0 }}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper' }}>
                          <SafeHtml html={page.content} />

                          <Box
                            sx={{
                              mt: 2,
                              pt: 2,
                              borderTop: '1px solid',
                              borderColor: 'divider',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              Last modified: {new Date(page.timemodified * 1000).toLocaleString()}
                            </Typography>
                            {module.url && (
                              <a
                                href={module.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontSize: '0.75rem' }}
                              >
                                View in Moodle →
                              </a>
                            )}
                          </Box>
                        </Paper>
                      </AccordionDetails>
                    </Accordion>
                  );
                }

                return (
                  <Paper
                    key={module.id}
                    elevation={1}
                    sx={{
                      p: 2,
                      mb: 2,
                      bgcolor: 'grey.50',
                      borderLeft: '4px solid',
                      borderColor: 'grey.400',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle1">{module.name}</Typography>
                      <Chip
                        label={module.modname}
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    </Box>

                    {module.description && (
                      <Box sx={{ mb: 2 }}>
                        <SafeHtml html={module.description} />
                      </Box>
                    )}

                    <Alert severity="info">
                      Preview for <strong>{module.modname}</strong> activities is not yet
                      implemented.{' '}
                      {module.url && (
                        <a href={module.url} target="_blank" rel="noopener noreferrer">
                          Open in Moodle →
                        </a>
                      )}
                    </Alert>
                  </Paper>
                );
              })}

              {modules.filter((m) => m.visible === 1).length === 0 && (
                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No visible activities in this section.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};